"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import AuthenticatedLayout from "../../Layouts/AuthenticatedLayout";
import InfoSection from "../../Components/InfoSection";
import GroupedContainer from "../../Components/GroupedContainer";
import SecondaryButton from "../../Components/SecondaryButton";
import axios from "axios";
import {
  TbSearch,
  TbX,
  TbCalendar,
  TbChevronDown,
  TbCheck,
  TbInfoCircle,
  TbCircleCheck,
  TbAlertCircle,
  TbDownload,
  TbFileText,
  TbPaperclip,
} from "react-icons/tb";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Snackbar (mesmo padrão visual que você já está usando)
 */
function Snackbar({ message, type = "success", onClose, duration = 5000 }) {
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 300);
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColor = {
    success: "bg-green-600 border-green-700",
    error: "bg-red-600 border-red-700",
    info: "bg-blue-600 border-blue-700",
    warning: "bg-amber-600 border-amber-700",
  }[type];

  const icon = {
    success: <TbCircleCheck className="w-5 h-5" />,
    error: <TbAlertCircle className="w-5 h-5" />,
    info: <TbInfoCircle className="w-5 h-5" />,
    warning: <TbInfoCircle className="w-5 h-5" />,
  }[type];

  return (
    <div
      className={`fixed bottom-4 right-4 z-50 transform transition-all duration-300 ${
        isVisible ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"
      }`}
    >
      <div
        className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg border max-w-sm flex items-start gap-3`}
      >
        <div className="flex-shrink-0 mt-0.5">{icon}</div>
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        <button
          onClick={() => {
            setIsVisible(false);
            setTimeout(onClose, 300);
          }}
          className="flex-shrink-0 text-white/80 hover:text-white ml-2"
        >
          <TbX className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

function Report() {
  const API_PREFIX =
    process.env.REACT_APP_API_PREFIX?.replace(/\/$/, "") || "https://new.cajuu.app/api";

  const userToken = useMemo(() => localStorage.getItem("token"), []);
  const headers = useMemo(() => {
    return {
      "Screen-Name": "/Load",
      Authorization: `Bearer ${userToken}`,
    };
  }, [userToken]);

  // Tabs:
  // 0=Internal Loads
  // 1=External Loads
  // 2=Driver Expenses (excludes Personal AND Owner Advance)
  // 3=Personal Expenses (only Personal)
  const tabs = useMemo(
    () => [
      { key: "internal", label: "Load Transports" },
      { key: "external", label: "External Loads" },
      { key: "expenses", label: "Expenses" }, // excludes Personal + Owner Advance (matches PDF)
      { key: "personal", label: "Personal Expenses" }, // only Personal
    ],
    []
  );

  const [activeTab, setActiveTab] = useState(0);

  // Overview totals
  const [overview, setOverview] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  // Drivers dropdown (custom search)
  const [drivers, setDrivers] = useState([]);
  const [driveSearch, setDriveSearch] = useState("");
  const [selectedDrive, setSelectedDrive] = useState("all");
  const [showDrivesList, setShowDrivesList] = useState(false);

  // Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Table listing
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, last_page: 1, per_page: 10, total: 0 });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    type: "success",
  });

  const driveSearchRef = useRef(null);

  const showSnackbar = (message, type = "success") => {
    setSnackbar({ open: true, message, type });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const formatCurrency = (amount) => {
    const n = Number(amount || 0);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(n);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const d = new Date(dateString);
      if (Number.isNaN(d.getTime())) return dateString;
      return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    } catch {
      return dateString;
    }
  };

  // Break Order ID every 5 chars (exactly)
  const renderChunkedText = (value, chunkSize = 5) => {
    const s = String(value ?? "");
    if (!s) return "—";
    const chunks = s.match(new RegExp(`.{1,${chunkSize}}`, "g")) || [s];

    return chunks.map((c, idx) => (
      <span key={idx}>
        {c}
        {idx < chunks.length - 1 ? <wbr /> : null}
      </span>
    ));
  };

  const buildQueryParams = () => {
    const params = {
      page,
      per_page: perPage,
    };

    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (selectedDrive !== "all") params.driver_id = selectedDrive;

    return params;
  };

  const buildFilterParamsNoPagination = () => {
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (selectedDrive !== "all") params.driver_id = selectedDrive;
    return params;
  };

  /**
   * FIX: showPicker() só pode ser chamado em um evento de "user gesture" (ex.: click/pointer).
   * Evitamos chamar no onFocus para não disparar NotAllowedError.
   */
  const safeShowDatePicker = (inputEl) => {
    if (!inputEl) return;
    if (typeof inputEl.showPicker !== "function") return;
    try {
      inputEl.showPicker();
    } catch (err) {
      // Ignora para não quebrar a UI
    }
  };

  const handleDateClick = (e) => {
    safeShowDatePicker(e.currentTarget);
  };

  const handleDateKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      safeShowDatePicker(e.currentTarget);
    }
  };

  const fetchDrivers = async () => {
    try {
      const res = await axios.get(`${API_PREFIX}/driver`, { headers });
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      const mapped = list.map((d) => {
        const fullName =
          d?.user?.first_name || d?.user?.last_name
            ? `${d?.user?.first_name || ""} ${d?.user?.last_name || ""}`.trim()
            : `Driver ${d?.id}`;
        return { id: d.id, name: fullName };
      });

      setDrivers(mapped);
    } catch (err) {
      console.error("Erro ao buscar drivers:", err);
      setDrivers([]);
      showSnackbar("Erro ao carregar drivers", "error");
    }
  };

  const fetchOverview = async () => {
    setOverviewLoading(true);
    try {
      const params = buildFilterParamsNoPagination();
      const res = await axios.get(`${API_PREFIX}/finance/overview`, {
        headers,
        params,
      });

      setOverview(res?.data?.data || null);
    } catch (err) {
      console.error("Erro ao buscar overview:", err);
      setOverview(null);
      showSnackbar("Erro ao carregar visão contábil", "error");
    } finally {
      setOverviewLoading(false);
    }
  };

  const getActiveEndpoint = () => {
    const key = tabs[activeTab]?.key;
    if (key === "internal") return `${API_PREFIX}/finance/loadtransports`;
    if (key === "external") return `${API_PREFIX}/finance/externalloads`;
    if (key === "personal") return `${API_PREFIX}/finance/personalexpenses`;
    return `${API_PREFIX}/finance/driverexpenses`; // expenses (non-personal, excluding Owner Advance)
  };

  const fetchTable = async () => {
    setLoading(true);
    try {
      const endpoint = getActiveEndpoint();
      const params = buildQueryParams();

      const res = await axios.get(endpoint, {
        headers,
        params,
      });

      const payload = res?.data?.data || null;

      const rows = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
        ? payload
        : [];
      setItems(rows);

      setMeta({
        current_page: payload?.current_page ?? 1,
        last_page: payload?.last_page ?? 1,
        per_page: payload?.per_page ?? perPage,
        total: payload?.total ?? rows.length ?? 0,
      });
    } catch (err) {
      console.error("Erro ao buscar tabela:", err);
      setItems([]);
      setMeta({ current_page: 1, last_page: 1, per_page: perPage, total: 0 });
      showSnackbar("Erro ao carregar lista", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDriveSelect = (driveId, driveName) => {
    setSelectedDrive(driveId);
    setDriveSearch(driveName);
    setShowDrivesList(false);
    setPage(1);
  };

  const clearDriveFilter = () => {
    setSelectedDrive("all");
    setDriveSearch("");
    setShowDrivesList(false);
    setPage(1);
  };

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setSelectedDrive("all");
    setDriveSearch("");
    setShowDrivesList(false);
    setPage(1);
    showSnackbar("Filtros limpos", "info");
  };

  const filteredDrives = useMemo(() => {
    return drivers.filter((d) => d.name.toLowerCase().includes(driveSearch.toLowerCase()));
  }, [drivers, driveSearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (driveSearchRef.current && !driveSearchRef.current.contains(event.target)) {
        setShowDrivesList(false);
        if (driveSearch.trim() === "" && selectedDrive !== "all") {
          setTimeout(() => setSelectedDrive("all"), 10);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [driveSearch, selectedDrive]);

  useEffect(() => {
    fetchDrivers();
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchOverview();
    fetchTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, page, perPage, startDate, endDate, selectedDrive]);

  const paginate = (pageNumber) => setPage(pageNumber);
  const nextPage = () => setPage((prev) => Math.min(prev + 1, meta.last_page || 1));
  const prevPage = () => setPage((prev) => Math.max(prev - 1, 1));

  // ✅ Normaliza URL vinda do backend (mais robusto: /https://..., https://site/https://site/..., etc)
  const normalizeExportUrl = (raw) => {
    if (!raw) return "";

    let u = String(raw).trim();

    // Caso clássico: vem "/https://...."
    if (u.startsWith("/https://") || u.startsWith("/http://")) {
      u = u.slice(1);
    }

    // Caso: "https://new.cajuu.app/https://new.cajuu.app/storage/..."
    // Pega a última ocorrência de http(s):// e corta dali
    const low = u.toLowerCase();
    const idxHttps = low.lastIndexOf("https://");
    const idxHttp = low.lastIndexOf("http://");
    const idx = Math.max(idxHttps, idxHttp);

    if (idx > 0) {
      u = u.slice(idx);
    }

    // Se for relativa, transforma em absoluta (mesma origem)
    if (!/^https?:\/\//i.test(u)) {
      if (u.startsWith("/")) return `${window.location.origin}${u}`;
      return `${window.location.origin}/${u}`;
    }

    return u;
  };

  const openDownloadInNewTab = (url, filename) => {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    if (filename) a.download = filename; // força download (mesma origem costuma respeitar)
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  /**
   * Export CSV agora retorna JSON com link:
   * { data: { url: "https://new.cajuu.app/storage/report_export/xxx.csv", file_name: "xxx.csv" } }
   */
  const downloadCsv = async (type) => {
    try {
      const params = buildFilterParamsNoPagination();

      let url = "";
      if (type === "internal") url = `${API_PREFIX}/finance/export/loadtransports`;
      if (type === "external") url = `${API_PREFIX}/finance/export/externalloads`;
      if (type === "expenses") url = `${API_PREFIX}/finance/export/driverexpenses`; // excludes Personal + Owner Advance
      if (type === "personal") url = `${API_PREFIX}/finance/export/personalexpenses`;

      const res = await axios.get(url, {
        headers,
        params,
      });

      const rawExportUrl = res?.data?.data?.url || res?.data?.url;
      const exportUrl = normalizeExportUrl(rawExportUrl);

      const fileName =
        res?.data?.data?.file_name ||
        res?.data?.data?.filename ||
        (type === "internal"
          ? "loadtransports.csv"
          : type === "external"
          ? "externalloads.csv"
          : type === "personal"
          ? "personalexpenses.csv"
          : "driverexpenses.csv");

      if (!exportUrl) {
        throw new Error("Export URL missing in response.");
      }

      // ✅ abre em nova guia e força download (sem sair da página)
      openDownloadInNewTab(exportUrl, fileName);

      showSnackbar("CSV gerado! Abrindo download em nova guia...", "success");
    } catch (err) {
      console.error("Erro ao exportar CSV:", err);
      showSnackbar("Erro ao exportar CSV", "error");
    }
  };

  /**
   * ✅ PDF export agora segue o MESMO padrão do CSV:
   * Backend retorna JSON: { data: { url, file_name } }
   * Front abre em nova guia para download (sem sair da página)
   */
  const downloadPdfReport = async () => {
    try {
      const params = buildFilterParamsNoPagination();
      const url = `${API_PREFIX}/finance/export/report`;

      const res = await axios.get(url, {
        headers,
        params,
      });

      const rawExportUrl = res?.data?.data?.url || res?.data?.url;
      const exportUrl = normalizeExportUrl(rawExportUrl);

      const fileName = res?.data?.data?.file_name || "report.pdf";

      if (!exportUrl) {
        throw new Error("Export URL missing in response.");
      }

      openDownloadInNewTab(exportUrl, fileName);

      showSnackbar("PDF gerado! Abrindo download em nova guia...", "success");
    } catch (err) {
      console.error("Erro ao exportar PDF:", err);
      showSnackbar("Erro ao exportar PDF", "error");
    }
  };

  const currentTabKey = tabs[activeTab]?.key;

  const renderAttachmentsLinks = (row) => {
    const attachments = Array.isArray(row?.attachments) ? row.attachments : [];

    if (!attachments.length) return "—";

    return (
      <div className="flex flex-wrap gap-2">
        {attachments.map((att, idx) => {
          const name = att?.original_name || att?.path || `Attachment ${idx + 1}`;
          const url =
            att?.public_url ||
            `https://new.cajuu.app/storage/${String(att?.path || "").replace(/^\/+/, "")}`;

          return (
            <a
              key={`${row?.id || "row"}-att-${idx}`}
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-zinc-600 bg-zinc-900/40 text-amber-400 hover:text-amber-300 hover:bg-zinc-900/70 transition-colors max-w-full"
              title={name}
            >
              <TbPaperclip className="text-base flex-shrink-0" />
              <span className="text-xs sm:text-sm truncate max-w-[220px]">{name}</span>
            </a>
          );
        })}
      </div>
    );
  };

  const renderTable = () => {
    if (currentTabKey === "internal") {
      return (
        <table className="min-w-full divide-y divide-zinc-600">
          <thead className="bg-[#1c1b19]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Order ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Pickup Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Vehicles
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Payment Rate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Broker Fee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Driver
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="bg-[#1c1b19]/50 divide-y divide-zinc-600">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                  </div>
                  <p className="mt-2 text-gray-400">Loading...</p>
                </td>
              </tr>
            ) : items.length > 0 ? (
              items.map((row) => {
                const driverName =
                  row?.driver?.user?.first_name || row?.driver?.user?.last_name
                    ? `${row?.driver?.user?.first_name || ""} ${row?.driver?.user?.last_name || ""}`.trim()
                    : row?.driver_id
                    ? `Driver ${row.driver_id}`
                    : "N/A";

                return (
                  <tr key={row.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-white break-all">
                      {renderChunkedText(row.order_id || row.id, 5)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {formatDate(row.pickup_date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {row.vehicles_summary || "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-500">
                      {formatCurrency(row.payment_rate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-500">
                      {formatCurrency(row.broker_fee)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{driverName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {row.load_status_name ?? row.load_status_id ?? "—"}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="7" className="px-6 py-8 text-center text-gray-400">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      );
    }

    if (currentTabKey === "external") {
      return (
        <table className="min-w-full divide-y divide-zinc-600">
          <thead className="bg-[#1c1b19]">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Order ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Pickup Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Vehicles
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Payment Rate
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Broker Fee
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Driver
              </th>
            </tr>
          </thead>
          <tbody className="bg-[#1c1b19]/50 divide-y divide-zinc-600">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                  </div>
                  <p className="mt-2 text-gray-400">Loading...</p>
                </td>
              </tr>
            ) : items.length > 0 ? (
              items.map((row) => {
                const driverName =
                  row?.driver?.user?.first_name || row?.driver?.user?.last_name
                    ? `${row?.driver?.user?.first_name || ""} ${row?.driver?.user?.last_name || ""}`.trim()
                    : row?.driver_id
                    ? `Driver ${row.driver_id}`
                    : "N/A";

                return (
                  <tr key={row.id} className="hover:bg-zinc-800/30 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-white break-all">
                      {renderChunkedText(row.order_id || row.id, 5)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                      {formatDate(row.pickup_date)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {row.vehicles_summary || "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-500">
                      {formatCurrency(row.payment_rate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-500">
                      {formatCurrency(row.broker_fee)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{driverName}</td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      );
    }

    // expenses (non-personal) OR personal
    const isPersonalTab = currentTabKey === "personal";

    return (
      <table className="min-w-full divide-y divide-zinc-600">
        <thead className="bg-[#1c1b19]">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Expense Date
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Category
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Description
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Amount
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Driver
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
              Attachments
            </th>
          </tr>
        </thead>
        <tbody className="bg-[#1c1b19]/50 divide-y divide-zinc-600">
          {loading ? (
            <tr>
              <td colSpan="6" className="px-6 py-8 text-center">
                <div className="flex justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                </div>
                <p className="mt-2 text-gray-400">Loading...</p>
              </td>
            </tr>
          ) : items.length > 0 ? (
            items.map((row) => {
              const driverName =
                row?.driver?.user?.first_name || row?.driver?.user?.last_name
                  ? `${row?.driver?.user?.first_name || ""} ${row?.driver?.user?.last_name || ""}`.trim()
                  : row?.driver_id
                  ? `Driver ${row.driver_id}`
                  : "N/A";

              return (
                <tr key={row.id} className="hover:bg-zinc-800/30 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {formatDate(row.expense_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                    {row.category || (isPersonalTab ? "Personal" : "—")}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300">
                    {row.description || "—"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-red-500">
                    {formatCurrency(row.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">{driverName}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">{renderAttachmentsLinks(row)}</td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="6" className="px-6 py-8 text-center text-gray-400">
                No records found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    );
  };

  const exportTypeForTab = () => {
    if (currentTabKey === "internal") return "internal";
    if (currentTabKey === "external") return "external";
    if (currentTabKey === "personal") return "personal";
    return "expenses";
  };

  const totals = overview?.totals || null;

  return (
    <AuthenticatedLayout>
      <div className="text-white text-base sm:text-lg md:text-xl lg:text-2xl p-4 sm:p-6 md:p-8">
        <GroupedContainer>
          <InfoSection title="FINANCE" description="Carrier Owner accounting overview" />
        </GroupedContainer>

        {/* Filters */}
        <div className="bg-[#1c1b19] p-4 sm:p-6 rounded-xl shadow-sm text-white border border-zinc-600 mt-4 mb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-lg sm:text-xl font-bold">Filters</h2>

            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={clearFilters}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1 justify-center sm:justify-start"
              >
                <TbX className="w-4 h-4" /> Clear Filters
              </button>

              <SecondaryButton
                onClick={downloadPdfReport}
                className="w-full sm:w-auto bg-green-600 text-black hover:bg-green-700 flex items-center gap-2 justify-center"
              >
                <TbFileText className="w-4 h-4" />
                Export PDF Report
              </SecondaryButton>

              <SecondaryButton
                onClick={() => downloadCsv(exportTypeForTab())}
                className="w-full sm:w-auto bg-amber-600 text-black hover:bg-amber-700 flex items-center gap-2 justify-center"
              >
                <TbDownload className="w-4 h-4" />
                Export CSV (current tab)
              </SecondaryButton>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {/* Driver filter - custom search dropdown */}
            <div className="relative" ref={driveSearchRef}>
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <TbSearch className="text-gray-400 text-sm" />
                </div>

                <input
                  type="text"
                  placeholder="Search drivers..."
                  value={driveSearch}
                  onChange={(e) => {
                    setDriveSearch(e.target.value);
                    if (e.target.value.trim() !== "") setShowDrivesList(true);
                  }}
                  onFocus={() => setShowDrivesList(true)}
                  className="w-full pl-10 pr-10 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg
                             focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                />

                {(driveSearch || selectedDrive !== "all") && (
                  <button
                    onClick={clearDriveFilter}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-white"
                  >
                    <TbX className="w-4 h-4" />
                  </button>
                )}
              </div>

              {showDrivesList && (
                <div className="absolute z-10 mt-2 w-full bg-[#1c1b19] border border-zinc-600 rounded-lg overflow-hidden shadow-lg">
                  <div className="max-h-48 overflow-y-auto">
                    <div
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleDriveSelect("all", "")}
                      className="px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between border-b border-zinc-600"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="px-2 py-1 text-center text-white text-sm">All Drivers</span>
                      </div>
                      {selectedDrive === "all" && (
                        <TbCheck className="text-amber-500 flex-shrink-0 ml-2" />
                      )}
                    </div>

                    {filteredDrives.length > 0 ? (
                      <>
                        {filteredDrives.map((d) => (
                          <div
                            key={d.id}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleDriveSelect(d.id.toString(), d.name)}
                            className="px-4 py-3 cursor-pointer hover:bg-zinc-800/50 transition-colors flex items-center justify-between border-b border-zinc-600 last:border-b-0"
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span
                                className="px-2 py-1 text-center text-white text-sm"
                                title={d.name}
                              >
                                {d.name}
                              </span>
                            </div>
                            {selectedDrive === d.id.toString() && (
                              <TbCheck className="text-amber-500 flex-shrink-0 ml-2" />
                            )}
                          </div>
                        ))}
                        <div className="px-4 py-2 text-xs text-gray-400 border-t border-zinc-600 bg-zinc-900/50">
                          {driveSearch
                            ? `Found ${filteredDrives.length} of ${drivers.length} drivers`
                            : `Showing all ${filteredDrives.length} drivers`}
                        </div>
                      </>
                    ) : (
                      <div className="px-4 py-3 text-center text-gray-400 text-sm">No drivers found</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Start Date */}
            <div className="relative">
              <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">
                Start Date
              </span>
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbCalendar className="text-blue-500 text-lg" />
              </div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(1);
                }}
                onClick={handleDateClick}
                onKeyDown={handleDateKeyDown}
                style={{ colorScheme: "dark" }}
                className="w-full pl-10 pr-4 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />
              <div className="absolute -top-2 left-1 text-xs text-gray-400 bg-[#1c1b19] px-1">
                Start Date
              </div>
            </div>

            {/* End Date */}
            <div className="relative">
              <span className="absolute -top-2 left-3 bg-[#1c1b19] px-1 text-xs text-gray-400">
                End Date
              </span>
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <TbCalendar className="text-blue-500 text-lg" />
              </div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(1);
                }}
                onClick={handleDateClick}
                onKeyDown={handleDateKeyDown}
                style={{ colorScheme: "dark" }}
                className="w-full pl-10 pr-4 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
              />
              <div className="absolute -top-2 left-1 text-xs text-gray-400 bg-[#1c1b19] px-1">
                End Date
              </div>
            </div>

            {/* Per page */}
            <div className="relative">
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                className="w-full pl-4 pr-12 bg-transparent text-white py-2 sm:py-3 text-sm border rounded-lg 
                           focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all 
                           appearance-none"
              >
                <option value={10} className="bg-[#1c1b19]">
                  10 / page
                </option>
                <option value={20} className="bg-[#1c1b19]">
                  20 / page
                </option>
                <option value={30} className="bg-[#1c1b19]">
                  30 / page
                </option>
                <option value={50} className="bg-[#1c1b19]">
                  50 / page
                </option>
              </select>
              <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                <TbChevronDown className="text-white text-lg" />
              </div>
            </div>
          </div>

          {/* Quick exports (✅ mobile: 2 por linha / ✅ desktop: 4 por linha) */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-2">
            <SecondaryButton
              onClick={() => downloadCsv("internal")}
              className="w-full text-white border-white hover:bg-white/10 flex items-center justify-center gap-2"
              variant="outline"
            >
              <TbDownload className="w-4 h-4" />
              Export LoadTransports CSV
            </SecondaryButton>

            <SecondaryButton
              onClick={() => downloadCsv("external")}
              className="w-full text-white border-white hover:bg-white/10 flex items-center justify-center gap-2"
              variant="outline"
            >
              <TbDownload className="w-4 h-4" />
              Export ExternalLoads CSV
            </SecondaryButton>

            <SecondaryButton
              onClick={() => downloadCsv("expenses")}
              className="w-full text-white border-white hover:bg-white/10 flex items-center justify-center gap-2"
              variant="outline"
            >
              <TbDownload className="w-4 h-4" />
              Export DriverExpenses CSV
            </SecondaryButton>

            <SecondaryButton
              onClick={() => downloadCsv("personal")}
              className="w-full text-white border-white hover:bg-white/10 flex items-center justify-center gap-2"
              variant="outline"
            >
              <TbDownload className="w-4 h-4" />
              Export PersonalExpenses CSV
            </SecondaryButton>
          </div>
        </div>

        {/* Totals cards (match PDF KPIs) */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4 mt-4">
          <div className="bg-[#1c1b19] border border-zinc-600 rounded-xl p-4">
            <div className="text-xs text-gray-400 uppercase tracking-wider">Internal Loads Total</div>
            <div className="text-lg sm:text-xl font-bold text-green-500 mt-1">
              {overviewLoading ? "..." : formatCurrency(totals?.internal_loads_payment_rate_total)}
            </div>
          </div>

          <div className="bg-[#1c1b19] border border-zinc-600 rounded-xl p-4">
            <div className="text-xs text-gray-400 uppercase tracking-wider">External Loads Total</div>
            <div className="text-lg sm:text-xl font-bold text-green-500 mt-1">
              {overviewLoading ? "..." : formatCurrency(totals?.external_loads_payment_rate_total)}
            </div>
          </div>

          <div className="bg-[#1c1b19] border border-zinc-600 rounded-xl p-4">
            <div className="text-xs text-gray-400 uppercase tracking-wider">
              Broker Fee Total <span className="text-gray-500">(Internal + External)</span>
            </div>
            <div className="text-lg sm:text-xl font-bold text-red-500 mt-1">
              {overviewLoading ? "..." : formatCurrency(totals?.broker_fee_total)}
            </div>
            <div className="text-xs text-gray-400 mt-2">
              Internal: {overviewLoading ? "..." : formatCurrency(totals?.internal_loads_broker_fee_total)}{" "}
              <span className="text-gray-600">|</span>{" "}
              External: {overviewLoading ? "..." : formatCurrency(totals?.external_loads_broker_fee_total)}
            </div>
          </div>

          <div className="bg-[#1c1b19] border border-zinc-600 rounded-xl p-4">
            <div className="text-xs text-gray-400 uppercase tracking-wider">
              Expenses Total <span className="text-gray-500">(excludes Personal + Owner Advance)</span>
            </div>
            <div className="text-lg sm:text-xl font-bold text-red-500 mt-1">
              {overviewLoading ? "..." : formatCurrency(totals?.driver_expenses_total)}
            </div>
          </div>

          <div className="bg-[#1c1b19] border border-zinc-600 rounded-xl p-4">
            <div className="text-xs text-gray-400 uppercase tracking-wider">
              Personal Expenses Total <span className="text-gray-500">(only Personal)</span>
            </div>
            <div className="text-lg sm:text-xl font-bold text-red-500 mt-1">
              {overviewLoading ? "..." : formatCurrency(totals?.personal_expenses_total)}
            </div>
          </div>

          <div className="bg-[#1c1b19] border border-zinc-600 rounded-xl p-4">
            <div className="text-xs text-gray-400 uppercase tracking-wider">Net Total</div>
            <div className="text-lg sm:text-xl font-bold text-white mt-1">
              {overviewLoading ? "..." : formatCurrency(totals?.net_total)}
            </div>
            <div className="text-xs text-gray-400 mt-2">Net excludes Owner Advance (matches PDF)</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 bg-[#1c1b19] border border-zinc-600 rounded-xl p-2">
          <div className="flex flex-col sm:flex-row gap-2">
            {tabs.map((t, idx) => (
              <button
                key={t.key}
                onClick={() => {
                  setActiveTab(idx);
                  setPage(1);
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
                  activeTab === idx
                    ? "bg-amber-600 text-black border-amber-700"
                    : "bg-transparent text-white border-zinc-600 hover:bg-zinc-800/30"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        <div className="mt-4 overflow-x-auto rounded-xl border border-zinc-600">{renderTable()}</div>

        {/* Pagination */}
        {meta?.total > 0 && (
          <>
            <div className="flex items-center justify-center space-x-2 mt-6">
              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={prevPage}
                disabled={(meta?.current_page ?? 1) === 1}
                className="text-white border-white hover:bg-white/10"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </SecondaryButton>

              <div className="flex items-center space-x-1">
                {(() => {
                  const currentPage = meta?.current_page ?? 1;
                  const totalPages = meta?.last_page ?? 1;

                  let startPage = Math.max(1, currentPage - 2);
                  const endPage = Math.min(totalPages, startPage + 4);

                  if (endPage - startPage < 4 && startPage > 1) {
                    startPage = Math.max(1, endPage - 4);
                  }

                  const pageNumbers = [];
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <SecondaryButton
                        key={i}
                        size="sm"
                        onClick={() => paginate(i)}
                        className={
                          currentPage === i
                            ? "bg-amber-600 text-black hover:bg-amber-700"
                            : "text-white border-white hover:bg-white/10"
                        }
                      >
                        {i}
                      </SecondaryButton>
                    );
                  }
                  return pageNumbers;
                })()}
              </div>

              <SecondaryButton
                variant="outline"
                size="sm"
                onClick={nextPage}
                disabled={(meta?.current_page ?? 1) >= (meta?.last_page ?? 1)}
                className="text-white border-white hover:bg-white/10"
              >
                Next
                <ChevronRight className="h-4 h-4 ml-1" />
              </SecondaryButton>
            </div>

            <div className="text-center text-sm text-white/70 mt-2">
              Showing{" "}
              {((meta?.current_page ?? 1) - 1) * (meta?.per_page ?? perPage) + 1}-
              {Math.min((meta?.current_page ?? 1) * (meta?.per_page ?? perPage), meta?.total ?? 0)} of{" "}
              {meta?.total ?? 0} records
            </div>
          </>
        )}
      </div>

      {/* Snackbar */}
      {snackbar.open && (
        <Snackbar message={snackbar.message} type={snackbar.type} onClose={closeSnackbar} duration={5000} />
      )}
    </AuthenticatedLayout>
  );
}

export default Report;
